import { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { useGlobalModules } from 'contexts';
import { formatPriceByStoreLocale } from 'lib/shopify';
import { get, set, keys } from 'lib/utils/sessionStorage';
import { parseGiftDiscounts, parseTieredDiscounts } from 'lib/cart/incentives/parseDiscounts';
import normalizeIncentives from 'lib/cart/incentives/normalizeIncentives';
import reconcileGiftLines, {
  parseRemovedIds,
  pruneExpiredRemovals,
  findGiftLineForGroup,
} from 'lib/cart/incentives/reconcileGiftLines';
import { useCart } from './useCart';
import { useUpdateItemQuantity } from './useUpdateItemQuantity';
import { useAddItemsToCart } from './useAddItemsToCart';

// SWR spreads an array key into args; always resolve to an array of payloads
// (a single discount comes back as [payload]). asActiveArray in parseDiscounts
// normalizes either shape, so downstream parsing is unaffected.
export const fetcher = (...urls) => Promise.all(urls.map(u => fetch(u).then(r => r.json())));

export const discountUrls = (ids, route, locale) =>
  (ids || []).map(id => `/api/shopify/${route}/?discountId=${id}&locale=${locale}`);

// Unified incentives engine (#742 Phase 2). Mounted ONLY by the new cart
// drawer (flag-on path); the old drawer keeps the legacy discount hooks.
export function useCartIncentives(locale) {
  const globalModules = useGlobalModules();
  const { settings, translations } = globalModules || {};
  const { cart, isEmpty } = useCart();
  const updateItemQuantity = useUpdateItemQuantity();
  const addItems = useAddItemsToCart();

  const { data: freeItemDiscounts } = useSWR(
    discountUrls(settings?.freeItem?.discountIDs, 'get-automatic-discount-by-id', locale),
    fetcher
  );
  const tieredUrls = discountUrls(
    settings?.tieredDiscount?.discountIDs,
    'get-tiered-discount-by-id',
    locale
  );
  const { data: tieredDiscounts } = useSWR(tieredUrls.length ? tieredUrls : null, fetcher);

  const raw = get(keys.AUTO_ADDED_FREE_ITEM_REMOVED);
  const removedIds = parseRemovedIds(raw);

  // Never reconcile against missing data: until every configured discount
  // fetch has resolved, conditions would be [] and the cleanup would strip
  // valid gift lines (legacy guarded this via validFreeProducts === null)
  const freeIds = settings?.freeItem?.discountIDs || [];
  const tieredIds = settings?.tieredDiscount?.discountIDs || [];
  const discountsReady =
    (!freeIds.length || freeItemDiscounts !== undefined) &&
    (!tieredIds.length || tieredDiscounts !== undefined);
  const cartLoaded = Boolean(cart?.lines);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { incentives, progressModel } = useMemo(
    () =>
      normalizeIncentives({
        // While the cart is still loading, legacy showed the plain shipping
        // bar (progress functions returned null without lineItems) — feed no
        // conditions until the cart is there
        freeItemConditions:
          cartLoaded && freeIds.length ? parseGiftDiscounts(freeItemDiscounts || []) : [],
        shopifyTiers: cartLoaded ? parseTieredDiscounts(tieredDiscounts || []) : [],
        tierMirrorEntries: cartLoaded ? settings?.tieredDiscount?.tiers : [],
        tierMirrorCollectionId: settings?.tieredDiscount?.tiersCollection,
        cartLines: cart?.lines?.edges,
        subTotalPrice: cart?.cost?.subtotalAmount?.amount,
        freeShippingPrice: parseFloat(settings?.shipping?.freeShippingPrice || 0),
        hasShippingBar: settings?.locale?.hasShippingBar !== false,
        translations,
        locale,
        formatPrice: formatPriceByStoreLocale,
      }),
    [
      freeItemDiscounts,
      tieredDiscounts,
      cart?.lines?.edges,
      cart?.cost?.subtotalAmount?.amount,
      cartLoaded,
      settings,
      translations,
      locale,
      raw,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!discountsReady || !cartLoaded || !removedIds.length) return;
    const cleaned = pruneExpiredRemovals(removedIds, incentives);
    if (cleaned.length !== removedIds.length) {
      set(keys.AUTO_ADDED_FREE_ITEM_REMOVED, JSON.stringify(cleaned));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentives, raw, discountsReady, cartLoaded]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const decisions = useMemo(
    () =>
      reconcileGiftLines({
        lines: cart?.lines?.edges,
        incentives,
        removedIds,
        scaleGiftQuantity: Boolean(settings?.freeItem?.scaleGiftQuantity),
      }),
    [cart?.lines?.edges, incentives, raw, settings] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ONE debounced reconciliation pass per settled cart state, lock-guarded.
  // Replaces the popup's auto-add effect and the legacy hook's 1s setTimeout
  // cleanup with a single cancellable timer — the debounce absorbs transient
  // mid-mutation cart states (qty steppers, related-product scaling).
  const reconciling = useRef(false);
  useEffect(() => {
    const { itemsToAdd, itemsToRemove, itemsToUpdate } = decisions;
    if (!discountsReady || !cartLoaded) return undefined;
    if (!itemsToAdd.length && !itemsToRemove.length && !itemsToUpdate.length) return undefined;

    const timeout = setTimeout(async () => {
      if (reconciling.current) return;
      reconciling.current = true;
      try {
        const quantityChanges = [...itemsToRemove, ...itemsToUpdate];
        if (quantityChanges.length) {
          await updateItemQuantity(quantityChanges);
        }
        if (itemsToAdd.length) {
          await addItems(itemsToAdd);
        }
      } catch {
        // Mutation hooks resync the cart via SWR; the next pass converges
      } finally {
        reconciling.current = false;
      }
    }, 600);
    return () => clearTimeout(timeout);
  }, [decisions, discountsReady, cartLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // User picks/swaps a gift in the GiftChoicePanel
  const selectGift = async (group, product) => {
    if (reconciling.current) return;
    const currentLine = findGiftLineForGroup(cart?.lines?.edges, group);

    if (currentLine?.merchandise?.id === product.variantId) return;
    reconciling.current = true;
    const attributes = [
      { key: '_freeItem', value: 'true' },
      { key: '_freeItemDiscountId', value: group.discountId },
    ];
    try {
      if (currentLine) {
        await updateItemQuantity([
          {
            id: currentLine.id,
            variantId: product.variantId,
            quantity: currentLine.quantity || 1,
            customAttributes: attributes,
          },
        ]);
      } else {
        await addItems([
          { variantId: product.variantId, quantity: 1, customAttributes: attributes },
        ]);
      }
    } finally {
      reconciling.current = false;
    }
  };

  return {
    incentives,
    progressModel,
    choiceGroups: decisions.choiceGroups,
    selectGift,
    isEmpty,
    cart,
  };
}

export default useCartIncentives;
